// src/components/GMBountyControls.jsx
//
// Dossier-themed GM edit panel. Points escalation only.
// Field notes editable. All changes logged to bounty_audit_log.

import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'

const D = {
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body: { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
}

async function logAudit(bountyId, userId, action, fieldName = null, oldVal = null, newVal = null) {
  await supabase.from('bounty_audit_log').insert({
    bounty_id: bountyId,
    changed_by: userId,
    action,
    field_name: fieldName,
    old_value: oldVal !== null ? String(oldVal) : null,
    new_value: newVal !== null ? String(newVal) : null,
  }).then(({ error }) => {
    if (error) console.error('[audit] Failed to log:', error.message)
  })
}

export default function GMBountyControls({ bounty, userId, onUpdate }) {
  const [editing, setEditing] = useState(false)
  const [points, setPoints] = useState(bounty.points)
  const [maxClaims, setMaxClaims] = useState(bounty.max_claims ?? '')
  const [notes, setNotes] = useState(bounty.notes || '')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  const save = async () => {
    setSaving(true)
    setMessage('')
    const newPoints = parseInt(points) || 0
    const newMaxClaims = maxClaims === '' ? null : parseInt(maxClaims) || 1

    if (newPoints < bounty.points) {
      setMessage('Points can only increase. To reduce, cancel and reissue.')
      setSaving(false)
      return
    }

    const updates = {}
    const audits = []

    if (newPoints !== bounty.points) {
      updates.points = newPoints
      audits.push({ field: 'points', old: bounty.points, new: newPoints, action: 'escalate' })
    }
    if (newMaxClaims !== bounty.max_claims) {
      updates.max_claims = newMaxClaims
      audits.push({ field: 'max_claims', old: bounty.max_claims ?? 'unlimited', new: newMaxClaims ?? 'unlimited', action: 'edit' })
    }
    if (notes !== (bounty.notes || '')) {
      updates.notes = notes || null
      audits.push({ field: 'notes', old: bounty.notes || '(empty)', new: notes || '(empty)', action: 'edit' })
    }

    if (Object.keys(updates).length === 0) {
      setMessage('No changes detected.')
      setSaving(false)
      return
    }

    const { error } = await supabase.from('bounties').update(updates).eq('id', bounty.id)
    if (error) {
      setMessage('Failed: ' + error.message)
    } else {
      for (const a of audits) {
        await logAudit(bounty.id, userId, a.action, a.field, a.old, a.new)
      }
      setMessage('Updated — changes logged.')
      setEditing(false)
      onUpdate()
    }
    setSaving(false)
  }

  const activateBounty = async () => {
    setSaving(true)
    const updates = { status: 'active' }
    if (bounty.bounty_type === 'defend') {
      updates.phase = 'prep'
      updates.phase_started_at = new Date().toISOString()
    }
    const { error } = await supabase.from('bounties').update(updates).eq('id', bounty.id)
    if (!error) {
      await logAudit(bounty.id, userId, 'activate', 'status', 'queued', 'active')
      onUpdate()
    } else {
      setMessage('Failed: ' + error.message)
    }
    setSaving(false)
  }

  const cancelBounty = async () => {
    if (!window.confirm('Cancel this bounty? This action is logged and cannot be undone.')) return
    setSaving(true)
    const { error } = await supabase
      .from('bounties')
      .update({ status: 'cancelled', claimed_at: new Date().toISOString() })
      .eq('id', bounty.id)
    if (error) {
      setMessage('Failed: ' + error.message)
    } else {
      await logAudit(bounty.id, userId, 'cancel', 'status', bounty.status, 'cancelled')
      onUpdate()
    }
    setSaving(false)
  }

  const pauseBounty = async () => {
    const newStatus = bounty.status === 'paused' ? 'active' : 'paused'
    setSaving(true)
    const { error } = await supabase
      .from('bounties')
      .update({ status: newStatus })
      .eq('id', bounty.id)
    if (error) {
      setMessage('Failed: ' + error.message)
    } else {
      await logAudit(bounty.id, userId, newStatus === 'paused' ? 'pause' : 'resume', 'status', bounty.status, newStatus)
      onUpdate()
    }
    setSaving(false)
  }

  if (!['active', 'paused', 'queued'].includes(bounty.status)) return null

  return (
    <div style={{
      background: 'rgba(168,42,31,.04)',
      border: '1px dashed rgba(168,42,31,.3)',
      padding: '12px 14px',
      marginTop: 14,
    }}>
      <div style={{
        ...D.display,
        fontSize: 10,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: '#a82a1f',
        marginBottom: 8,
      }}>
        ★ GM CONTROLS — ALL CHANGES LOGGED
      </div>

      {message && (
        <div style={{
          ...D.body, fontSize: 11,
          color: message.includes('Failed') || message.includes('only increase') ? '#a82a1f' : '#6a8a5a',
          marginBottom: 8, fontStyle: 'italic',
        }}>
          {message}
        </div>
      )}

      {!editing ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setEditing(true)} disabled={saving}
            style={{
              ...D.display, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
              background: '#1f1d18', color: '#f0e6c8', border: 'none',
              padding: '6px 14px', cursor: 'pointer',
            }}>
            EDIT BOUNTY
          </button>
          {bounty.status === 'queued' && !bounty.parent_frago_id && (
            <button onClick={activateBounty} disabled={saving}
              style={{
                ...D.display, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                background: '#6a8a5a', color: '#f0e6c8', border: 'none',
                padding: '6px 14px', cursor: 'pointer',
              }}>
              ACTIVATE
            </button>
          )}
          {!bounty.parent_frago_id && (
            <button onClick={pauseBounty} disabled={saving}
              style={{
                ...D.display, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                background: bounty.status === 'paused' ? '#6a8a5a' : '#cbb98c',
                color: bounty.status === 'paused' ? '#f0e6c8' : '#1f1d18',
                border: 'none', padding: '6px 14px', cursor: 'pointer',
              }}>
              {bounty.status === 'paused' ? 'RESUME' : 'PAUSE'}
            </button>
          )}
          {!bounty.parent_frago_id && (
            <button onClick={cancelBounty} disabled={saving}
              style={{
                ...D.display, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                background: '#a82a1f', color: '#f0e6c8', border: 'none',
                padding: '6px 14px', cursor: 'pointer',
              }}>
              CANCEL BOUNTY
            </button>
          )}
          {bounty.parent_frago_id && (
            <span style={{
              ...D.body, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: '#7a7058', padding: '6px 14px', border: '1px dashed #7a7058',
            }} title="Lifecycle owned by parent FRAGO — manual transitions disabled.">
              ⛓ LINKED — PARENT FRAGO OWNS LIFECYCLE
            </span>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Points (escalation only) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ ...D.body, color: '#7a7058', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: 80 }}>
              POINTS ↑
            </label>
            <input
              type="number"
              value={points}
              min={bounty.points}
              onChange={e => setPoints(e.target.value)}
              style={{
                ...D.body, fontSize: 12, width: 80,
                background: '#f0e6c8', border: '1px solid #cbb98c',
                padding: '4px 8px', color: '#1f1d18',
              }}
            />
            <span style={{ ...D.body, fontSize: 9, color: '#7a7058' }}>min {bounty.points} (current)</span>
          </div>

          {/* Max Claims */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ ...D.body, color: '#7a7058', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', minWidth: 80 }}>
              MAX CLAIMS
            </label>
            <input
              type="number"
              value={maxClaims}
              onChange={e => setMaxClaims(e.target.value)}
              placeholder="∞"
              style={{
                ...D.body, fontSize: 12, width: 80,
                background: '#f0e6c8', border: '1px solid #cbb98c',
                padding: '4px 8px', color: '#1f1d18',
              }}
            />
            <span style={{ ...D.body, fontSize: 9, color: '#7a7058' }}>blank = unlimited</span>
          </div>

          {/* Field Notes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ ...D.body, color: '#7a7058', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              FIELD NOTES
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Operational notes, intel, target description..."
              rows={3}
              style={{
                ...D.body, fontSize: 11, width: '100%',
                background: '#f0e6c8', border: '1px solid #cbb98c',
                padding: '6px 8px', color: '#1f1d18', resize: 'vertical',
                lineHeight: 1.5,
              }}
            />
          </div>

          {/* Save / Cancel */}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button onClick={save} disabled={saving}
              style={{
                ...D.display, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                background: '#1f1d18', color: '#f0e6c8', border: 'none',
                padding: '6px 14px', cursor: 'pointer',
              }}>
              {saving ? 'SAVING...' : 'SAVE'}
            </button>
            <button onClick={() => { setEditing(false); setPoints(bounty.points); setMaxClaims(bounty.max_claims ?? ''); setNotes(bounty.notes || ''); setMessage('') }}
              style={{
                ...D.display, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                background: 'none', color: '#7a7058', border: '1px solid #cbb98c',
                padding: '6px 14px', cursor: 'pointer',
              }}>
              CANCEL
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
